import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  NotificationEvents,
  NotificationService,
} from "@service/notification.service";
import IncomingCallDialog from "@components/IncomingCall/IncomingCallDialog";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetRemindersQuery } from "@store/reminderApi";
import { Reminder } from "@models/reminder";
import { setOpen } from "@store/incomingCallSlice";
import { useNavigate } from "react-router-dom";

const INCOMING_CALL_TIMEOUT = 30 * 1000;

const notificationService = NotificationService.instance;
const AUDIO_FILE_PATH = `${process.env.PUBLIC_URL}/audio/ring-phone.mp3`;

const IncomingCall: FC = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isOpen = useSelector((state: RootState) => state.incomingCall.open);
  const [loading, setLoading] = useState(false);
  const currentReminderIdRef = useRef<string | null>(null);
  const [currentReminder, setCurrentReminder] = useState<Reminder | null>(null);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: reminders } = useGetRemindersQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const playRingAudio = useCallback((): void => {
    if (!audioRef.current) {
      const audio = new Audio(AUDIO_FILE_PATH);
      audioRef.current = audio;
      audio.loop = true;
    }

    const p = audioRef.current.play();
    if (p !== undefined) {
      void p.catch(() => {
        // pause() or tab policy before play() settled — safe to ignore
      });
    }
  }, []);

  const stopAudio = useCallback((): void => {
    audioRef.current?.pause();
  }, []);

  const openDialog = useCallback(
    (reminder: Reminder) => {
      currentReminderIdRef.current = reminder.id;
      setCurrentReminder(reminder);
      playRingAudio();
      dispatch(setOpen(true));
    },
    [playRingAudio, dispatch],
  );

  const onMessage = useCallback(
    (e: MessageEvent) => {
      if (currentReminderIdRef.current) {
        return;
      }
      const data = e.data;

      if (!data || data.type !== NotificationEvents.NOTIFICATION || !data.id) {
        return;
      }

      const reminder = reminders?.data.find(({ id }) => id === data.id);
      if (!reminder) {
        return;
      }

      openDialog(reminder);
      setTimeout(() => {
        if (currentReminderIdRef.current === data.id) {
          dispatch(setOpen(false));
        }
      }, INCOMING_CALL_TIMEOUT);
    },
    [openDialog, dispatch, reminders],
  );

  useEffect(() => {
    const _handler = onMessage;
    notificationService.subscribeToMessages(_handler);

    return () => {
      notificationService.unsubscribeFromMessages(_handler);
    };
  }, [onMessage]);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      currentReminderIdRef.current = null;
      setCurrentReminder(null);
      stopAudio();
    }
  }, [isOpen, stopAudio]);

  const onAccept = useCallback(() => {
    if (!currentReminder) {
      return;
    }
    setLoading(true);
    navigate(`/portal/talk`, {
      state: {
        reminderId: currentReminder.id,
        activityType: currentReminder.activityType,
      },
    });
    stopAudio();
  }, [currentReminder, stopAudio, navigate]);

  const onClose = useCallback(() => {
    dispatch(setOpen(false));
  }, [dispatch]);

  return (
    <IncomingCallDialog
      open={isOpen}
      loading={loading}
      onClose={onClose}
      onAccept={onAccept}
      reminder={currentReminder}
    />
  );
};

export default IncomingCall;
